import { FunctionComponent, useState, useRef, useEffect } from "react";
import TextOptionBox, { TextOptionBoxProps } from "./TextOptionBox";
import Column from "../Column/Column";
import Button from "../Button/Button";

export interface TextOptionBoxesData {
  id: string;
  title: string;
  heading: string;
  description: string;
  encrypt?: boolean;
  addToScore?: boolean;
  type: string;
  validations: {
    required?: boolean;
  };
  components: Array<TextOptionBoxProps>;
  properties: {
    allowMultipleSelection: boolean;
    allowOtherChoice: boolean;
    randomize: boolean;
    verticalAlignment: boolean;
  };
}

type TextOptionBoxesProps = {
  data: TextOptionBoxesData;
  onDataSelected: Function;
  onBack?: Function;
  errors: { [key: string]: String };
  isActive: boolean;
  nextPage?: string;
  isLoading?: boolean;
};

interface SelectProps {
  id: string;
  label: string;
  value?: number;
}

const TextOptionBoxes: FunctionComponent<TextOptionBoxesProps> = ({
  data,
  onDataSelected,
  onBack,
  isActive,
  errors,
  nextPage,
  isLoading,
}) => {
  const [choices, setChoices] = useState<Array<SelectProps>>([]);
  const [choicesArray, setChoicesArray] = useState<Array<string>>([]);

  const ref = useRef(choices);
  useEffect(() => {
    ref.current = choices;
  }, [choices]);

  useEffect(() => {
    if (isActive) {
      document.addEventListener("keydown", onContinue);
      return () => {
        document.removeEventListener("keydown", onContinue);
      };
    }
    return;
  }, [isActive]);

  const onContinue = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.keyCode === 13) {
      onSelectData();
    }
  };
  const onBoxSelected = ({ id, label, value }: SelectProps) => {
    const index = choices.findIndex((el) => el.id === id);
    if (data.properties.allowMultipleSelection) {
      if (index === -1) {
        setChoices((current) => [...current, { id, label, value }]);
        setChoicesArray((current) => [...current, id]);
      } else {
        setChoices((current) => [
          ...current.slice(0, index),
          ...current.slice(index + 1),
        ]);
        setChoicesArray((current) => [
          ...current.slice(0, index),
          ...current.slice(index + 1),
        ]);
      }
    } else {
      setChoices([{ id, label, value }]);
      setChoicesArray([id]);
      onSelectData({ id, label, value });
    }
  };
  const content: JSX.Element[] = [];
  data.components.forEach(
    (el) =>
      (content[el.index] = (
        <div className="px-2 py-2 w-1/2 lg:py-0 lg:px-4 lg:w-1/4" key={el.id}>
          <TextOptionBox
            {...el}
            onSelect={() =>
              onBoxSelected({ id: el.id, label: el.heading, value: el.value })
            }
            questionId={data.id}
            isSelected={choicesArray.includes(el.id)}
          />
        </div>
      ))
  );
  const onSelectData = (customAnswer?: { [key: string]: any }) => {
    let answer: { [key: string]: any } = {};

    if (customAnswer) {
      answer[customAnswer.id] = customAnswer;
    } else {
      ref.current.forEach((el) => {
        answer[el.id] = el;
      });
    }
    const selectedData = {
      type: "multiple_choice",
      page: { type: "text_multiple_choice", id: data.id },
      data: answer,
    };
    onDataSelected(selectedData);
  };
  return (
    <div>
      <div className="flex flex-wrap items-stretch mb-12 -mx-2 lg:-mx-4">
        {content}
      </div>
      {errors?.general ? (
        <p className="text-center mb-4 -mt-6 text-red-500">{errors.general}</p>
      ) : null}
      <div className="flex flex-wrap justify-center mb-12 -mx-2 lg:-mx-4">
        {onBack ? (
          <div className="px-2 lg:py-0 lg:px-4 lg:w-1/4">
            <Button variant="outline" onPress={() => onBack()}>
              Zurück
            </Button>
          </div>
        ) : null}
        {data.properties.allowMultipleSelection ? (
          <div className="px-2 lg:py-0 lg:px-4 lg:w-1/4">
            <Button
              variant="primary"
              onPress={() => onSelectData()}
              isLoading={isLoading}
            >
              {nextPage ? "Weiter" : "Anmelden"}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TextOptionBoxes;
